import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FLASH_MESSAGES } from '../../../core/constants/flash-messages';
import {
  apiErrorMessage,
  apiErrorMessageAsync,
} from '../../../core/http/api-error-message';
import { DocumentAchatService } from '../../../core/services/document-achat.service';
import { FlashService } from '../../../core/services/flash.service';
import { formatDateFr } from '../../../core/models/logement.model';
import {
  DocumentAchatLigne,
  DocumentAchatType,
  lignesDocumentsAchat,
} from '../../../core/models/document-achat.model';
import { DocumentViewerSession } from '../../../core/utils/document-viewer.util';
import { DocumentDeleteSession } from '../../../core/utils/document-delete.util';
import { DocumentDeleteDialogComponent } from '../../../shared/document-delete-dialog/document-delete-dialog.component';
import { DocumentViewerDialogComponent } from '../../../shared/document-viewer-dialog/document-viewer-dialog.component';
import { HelpTipComponent } from '../../../shared/help-tip/help-tip.component';

/**
 * Liste des documents d'achat d'un bien (compromis / attestation) :
 * upload, aperçu et suppression.
 */
@Component({
  selector: 'app-documents-achat',
  templateUrl: './documents-achat.component.html',
  styleUrls: ['./documents-achat.component.css'],
  imports: [
    FaIconComponent,
    TranslateModule,
    ReactiveFormsModule,
    HelpTipComponent,
    DocumentViewerDialogComponent,
    DocumentDeleteDialogComponent,
  ],
})
export class DocumentsAchatComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) bienId!: string;

  @ViewChild('uploadDialog')
  private readonly uploadDialog?: ElementRef<HTMLDialogElement>;

  @ViewChild(DocumentDeleteDialogComponent)
  private readonly deleteDialog?: DocumentDeleteDialogComponent;

  @ViewChild(DocumentViewerDialogComponent)
  private readonly viewerDialog?: DocumentViewerDialogComponent;

  lignes: DocumentAchatLigne[] = lignesDocumentsAchat([]);
  loading = false;
  loadError = false;
  listReloadError = false;

  uploadType?: DocumentAchatType;
  uploadFile?: File;
  uploading = false;
  uploadErrorMessage: string | null = null;
  uploadFileError = false;

  readonly deleteSession = new DocumentDeleteSession<DocumentAchatLigne>();
  readonly viewer: DocumentViewerSession;

  readonly uploadForm = this.fb.nonNullable.group({
    dateDocument: [''],
  });

  constructor(
    private readonly documentAchatService: DocumentAchatService,
    private readonly flashService: FlashService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    sanitizer: DomSanitizer
  ) {
    this.viewer = new DocumentViewerSession(sanitizer);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bienId'] && this.bienId) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.viewer.revoke();
  }

  get isBusy(): boolean {
    return this.uploading || this.deleteSession.deleting || this.viewer.loading;
  }

  get uploadIsReplace(): boolean {
    return !!this.lignes.find((ligne) => ligne.type === this.uploadType)
      ?.document;
  }

  get deleteTypeLabel(): string {
    return this.deleteSession.ligne?.type ?? '';
  }

  get deleteFileName(): string {
    return this.deleteSession.ligne?.document?.fileNameDto?.trim() || '';
  }

  fileLabel(ligne: DocumentAchatLigne): string {
    return ligne.document?.fileNameDto?.trim() || '';
  }

  dateDocumentLabel(ligne: DocumentAchatLigne): string {
    return formatDateFr(ligne.document?.dateDocumentDto);
  }

  ligneOk(ligne: DocumentAchatLigne): boolean {
    return !!ligne.document;
  }

  openViewer(ligne: DocumentAchatLigne): void {
    const documentMeta = ligne.document;
    const documentId = documentMeta?.idDocumentDto;
    if (!this.bienId || !documentMeta || !documentId) {
      return;
    }

    this.viewer.open({
      title: ligne.type,
      fileName: documentMeta.fileNameDto,
      contentType: documentMeta.contentTypeDto,
      download$: this.documentAchatService.download(this.bienId, documentId),
      dialog: this.viewerDialog?.dialogElement,
      messages: this.viewerMessages(),
      resolveErrorAsync: (err) =>
        this.resolveApiErrorAsync(err, 'documentsAchat.viewError'),
    });
  }

  closeViewer(): void {
    this.viewer.close(this.viewerDialog?.dialogElement);
  }

  onViewerDialogCancel(event: Event): void {
    this.viewer.onDialogCancel(event, this.viewerDialog?.dialogElement);
  }

  openViewerExternally(): void {
    this.viewer.openExternally(this.translate.instant('documentsAchat.viewError'));
  }

  downloadViewerFile(): void {
    this.viewer.downloadFile(
      this.translate.instant('documentsAchat.documentFallback')
    );
  }

  openUpload(ligne: DocumentAchatLigne): void {
    this.uploadType = ligne.type;
    this.uploadFile = undefined;
    this.uploadErrorMessage = null;
    this.uploadFileError = false;
    this.uploading = false;
    this.uploadForm.reset({ dateDocument: '' });
    this.uploadDialog?.nativeElement.showModal();
  }

  cancelUpload(): void {
    this.uploadDialog?.nativeElement.close();
    this.uploadType = undefined;
    this.uploadFile = undefined;
    this.uploadErrorMessage = null;
    this.uploadFileError = false;
    this.uploading = false;
  }

  onUploadDialogCancel(event: Event): void {
    event.preventDefault();
    this.cancelUpload();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.uploadFile = input.files?.[0];
    this.uploadFileError = false;
    this.uploadErrorMessage = null;
  }

  submitUpload(): void {
    if (!this.bienId || !this.uploadType) {
      return;
    }
    this.uploadFileError = !this.uploadFile;
    if (!this.uploadFile) {
      return;
    }

    const dateDocument =
      this.uploadForm.controls.dateDocument.value.trim() || null;

    this.uploading = true;
    this.uploadErrorMessage = null;

    this.documentAchatService
      .upload(this.bienId, {
        type: this.uploadType,
        dateDocument,
        file: this.uploadFile,
      })
      .subscribe({
        next: () => {
          this.flashService.success(FLASH_MESSAGES.documentAchatUploade);
          this.cancelUpload();
          this.reloadDocuments();
        },
        error: (err) => {
          this.uploading = false;
          this.uploadErrorMessage = this.resolveApiError(
            err,
            'documentsAchat.uploadError'
          );
        },
      });
  }

  askDelete(ligne: DocumentAchatLigne): void {
    this.deleteSession.ask(
      ligne,
      ligne.document?.idDocumentDto,
      this.isBusy,
      this.deleteDialog
    );
  }

  cancelDelete(): void {
    this.deleteSession.cancel(this.deleteDialog);
  }

  onDeleteDialogCancel(event: Event): void {
    this.deleteSession.onDialogCancel(event, this.deleteDialog);
  }

  confirmDelete(): void {
    const documentId = this.deleteSession.ligne?.document?.idDocumentDto;
    if (!this.bienId || !documentId) {
      return;
    }

    this.deleteSession.startRequest();

    this.documentAchatService.delete(this.bienId, documentId).subscribe({
      next: () => {
        this.flashService.success(FLASH_MESSAGES.documentAchatSupprime);
        this.cancelDelete();
        this.reloadDocuments();
      },
      error: (err) => {
        this.deleteSession.fail(
          this.resolveApiError(err, 'documentsAchat.deleteError')
        );
      },
    });
  }

  private viewerMessages() {
    return {
      viewError: this.translate.instant('documentsAchat.viewError'),
      viewPdfMobile: this.translate.instant('documentsAchat.viewPdfMobile'),
      documentFallback: this.translate.instant('documentsAchat.documentFallback'),
    };
  }

  private resolveApiError(err: unknown, fallbackKey: string): string {
    return apiErrorMessage(err, this.translate.instant(fallbackKey));
  }

  private resolveApiErrorAsync(
    err: unknown,
    fallbackKey: string
  ): Promise<string> {
    return apiErrorMessageAsync(err, this.translate.instant(fallbackKey));
  }

  private reload(): void {
    if (!this.bienId) {
      return;
    }
    this.loading = true;
    this.loadError = false;
    this.listReloadError = false;
    this.documentAchatService.list(this.bienId).subscribe({
      next: (documents) => {
        this.lignes = lignesDocumentsAchat(documents);
        this.loading = false;
      },
      error: () => {
        this.lignes = lignesDocumentsAchat([]);
        this.loadError = true;
        this.loading = false;
      },
    });
  }

  private reloadDocuments(): void {
    if (!this.bienId) {
      return;
    }
    this.listReloadError = false;
    this.documentAchatService.list(this.bienId).subscribe({
      next: (documents) => {
        this.lignes = lignesDocumentsAchat(documents);
        this.loadError = false;
      },
      error: () => {
        this.listReloadError = true;
      },
    });
  }
}
